import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardBody, CardHeader, Table, Input, Button, Row, Col, Spinner, Alert, Badge, ButtonGroup } from 'reactstrap';
import Select from 'react-select';
import { comptabiliteActions } from '../../sagas/comptabiliteSlice';
import { FaSearch, FaDownload, FaPrint, FaCalendarAlt, FaFileAlt, FaCoins, FaUsers, FaTruck, FaBuilding } from 'react-icons/fa';
import { formatPrice } from '../../helpers/utility';

const ReleveCompte = () => {
    const dispatch = useDispatch();
    const { releveCompte, comptes, clientAccounts, supplierAccounts, loading, error } = useSelector(state => state.comptabilite);
    
    const [filters, setFilters] = useState({
        selectedCompte: null,
        dateDebut: '',
        dateFin: ''
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [accountType, setAccountType] = useState('general'); // 'general', 'clients', 'suppliers'

    useEffect(() => {
        // Load initial accounts list
        dispatch(comptabiliteActions.getComptes({ search: '' }));
        dispatch(comptabiliteActions.getClientAccounts());
        dispatch(comptabiliteActions.getSupplierAccounts());
    }, [dispatch]);

    // Load accounts based on selected type
    useEffect(() => {
        setFilters(prev => ({ ...prev, selectedCompte: null }));
    }, [accountType]);

    // Debounced search for accounts
    useEffect(() => {
        const delayedSearch = setTimeout(() => {
            if (searchTerm.length >= 0) {
                dispatch(comptabiliteActions.getComptes({ search: searchTerm }));
            }
        }, 300);

        return () => clearTimeout(delayedSearch);
    }, [searchTerm, dispatch]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleCompteChange = (selectedOption) => {
        setFilters(prev => ({
            ...prev,
            selectedCompte: selectedOption
        }));
    };

    const handleSearch = () => {
        if (!filters.selectedCompte) {
            alert('Veuillez sélectionner un compte');
            return;
        }
        
        dispatch(comptabiliteActions.getReleveCompte({
            numeroCompte: filters.selectedCompte.value,
            dateDebut: filters.dateDebut || null,
            dateFin: filters.dateFin || null
        }));
    };

    const exportToPDF = () => {
        console.log('Export to PDF functionality to be implemented');
    };

    const exportToExcel = () => {
        console.log('Export to Excel functionality to be implemented');
    };

    

    // Get current accounts based on selected type
    const getCurrentAccounts = () => {
        switch (accountType) {
            case 'clients':
                return clientAccounts;
            case 'suppliers':
                return supplierAccounts;
            default:
                return comptes;
        }
    };

    // Format options for react-select
    const compteOptions = getCurrentAccounts().map(compte => ({
        value: compte.numero,
        label: `${compte.numero} - ${compte.intitule}`,
        typeCompte: compte.typeCompte,
        soldeActuel: compte.soldeActuel,
        compteCollectif: compte.compteCollectif,
        codeTiers: compte.codeTiers,
        idTiers: compte.idTiers
    }));

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            borderColor: state.isFocused ? '#0d6efd' : '#ced4da',
            boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(13, 110, 253, 0.25)' : null,
            '&:hover': {
                borderColor: '#0d6efd'
            }
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#f8f9fa' : 'white',
            color: state.isSelected ? 'white' : '#495057'
        })
    };

    const formatOptionLabel = ({ label, typeCompte, soldeActuel, compteCollectif, codeTiers }) => (
        <div className="d-flex justify-content-between align-items-center">
            <div>
                <span>{label}</span>
                {compteCollectif && (
                    <div className="small text-muted">
                        <i className="mdi mdi-account-group me-1"></i>
                        Collectif: {compteCollectif} {codeTiers && `| Code: ${codeTiers}`}
                    </div>
                )}
            </div>
            <div className="d-flex align-items-center">
                <Badge color={typeCompte === 'ACTIF' ? 'success' : typeCompte === 'PASSIF' ? 'info' : typeCompte === 'CHARGE' ? 'warning' : 'primary'} className="me-2">
                    {typeCompte}
                </Badge>
                <small className="text-muted">{formatPrice(soldeActuel)}</small>
            </div>
        </div>
    );

    const getCurrentLoadingState = () => {
        switch (accountType) {
            case 'clients':
                return loading.clientAccounts;
            case 'suppliers':
                return loading.supplierAccounts;
            default:
                return loading.comptes;
        }
    };

    return (
        <div className="page-content" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-1">
                        <FaFileAlt className="me-2 text-primary" />
                        Relevé de Compte
                    </h4>
                    <p className="text-muted mb-0">Consultez les détails et mouvements de vos comptes</p>
                </div>
                <div className="d-flex gap-2">
                    <Button color="outline-secondary" onClick={exportToPDF} disabled={!releveCompte}>
                        <FaPrint className="me-1" /> PDF
                    </Button>
                    <Button color="outline-success" onClick={exportToExcel} disabled={!releveCompte}>
                        <FaDownload className="me-1" /> Excel
                    </Button>
                </div>
            </div>

            <Row>
                <Col lg={4} className="mb-3">
                    {/* Left column: Search Filters Card */}
                    <Card className="shadow-sm mb-3">
                        <CardHeader className="bg-light">
                            <div className="d-flex align-items-center">
                                <FaSearch className="me-2 text-primary" />
                                <h6 className="mb-0">Critères de Recherche</h6>
                            </div>
                        </CardHeader>
                        <CardBody>
                            <Row className="g-3">
                                <Col lg={12} className="mb-3">
                                    <label className="form-label fw-semibold">
                                        <FaBuilding className="me-1" />
                                        Type de Compte
                                    </label>
                                    <ButtonGroup className="w-100">
                                        <Button
                                            color={accountType === 'general' ? 'primary' : 'outline-primary'}
                                            onClick={() => setAccountType('general')}
                                            className="d-flex align-items-center justify-content-center"
                                        >
                                            <FaCoins className="me-1" />
                                            Comptes Généraux
                                        </Button>
                                        <Button
                                            color={accountType === 'clients' ? 'success' : 'outline-success'}
                                            onClick={() => setAccountType('clients')}
                                            className="d-flex align-items-center justify-content-center"
                                        >
                                            <FaUsers className="me-1" />
                                            Comptes Clients
                                        </Button>
                                        <Button
                                            color={accountType === 'suppliers' ? 'warning' : 'outline-warning'}
                                            onClick={() => setAccountType('suppliers')}
                                            className="d-flex align-items-center justify-content-center"
                                        >
                                            <FaTruck className="me-1" />
                                            Comptes Fournisseurs
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                                <Col lg={12} className="mb-2">
                                    <label className="form-label fw-semibold">
                                        <FaCoins className="me-1" />
                                        Compte *
                                    </label>
                                    <Select
                                        options={compteOptions}
                                        value={filters.selectedCompte}
                                        onChange={handleCompteChange}
                                        placeholder={`Sélectionner un compte ${accountType === 'clients' ? 'client' : accountType === 'suppliers' ? 'fournisseur' : 'général'}...`}
                                        isLoading={getCurrentLoadingState()}
                                        isSearchable
                                        onInputChange={setSearchTerm}
                                        formatOptionLabel={formatOptionLabel}
                                        styles={customSelectStyles}
                                        noOptionsMessage={() => "Aucun compte trouvé"}
                                        loadingMessage={() => "Chargement..."}
                                    />
                                </Col>
                                <Col lg={6} className="mb-2">
                                    <label className="form-label fw-semibold">
                                        <FaCalendarAlt className="me-1" />
                                        Date Début
                                    </label>
                                    <Input
                                        type="date"
                                        value={filters.dateDebut}
                                        onChange={(e) => handleFilterChange('dateDebut', e.target.value)}
                                        className="form-control"
                                    />
                                </Col>
                                <Col lg={6} className="mb-2">
                                    <label className="form-label fw-semibold">
                                        <FaCalendarAlt className="me-1" />
                                        Date Fin
                                    </label>
                                    <Input
                                        type="date"
                                        value={filters.dateFin}
                                        onChange={(e) => handleFilterChange('dateFin', e.target.value)}
                                        className="form-control"
                                    />
                                </Col>
                                <Col lg={12} className="d-flex align-items-end">
                                    <Button 
                                        color="primary" 
                                        onClick={handleSearch}
                                        disabled={loading.releveCompte || !filters.selectedCompte}
                                        className="w-100"
                                    >
                                        {loading.releveCompte ? (
                                            <>
                                                <Spinner size="sm" className="me-1" />
                                                Recherche...
                                            </>
                                        ) : (
                                            <>
                                                <FaSearch className="me-1" />
                                                Rechercher
                                            </>
                                        )}
                                    </Button>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    {/* Left column: Account Info */}
                    {releveCompte && (
                        <Card className="shadow-sm">
                            <CardHeader className="bg-primary text-white py-2">
                                <div className="d-flex align-items-center">
                                    <FaCoins className="me-2" />
                                    <h6 className="mb-0">Informations du Compte</h6>
                                </div>
                            </CardHeader>
                            <CardBody className="py-3">
                                <Row>
                                    <Col lg={12}>
                                        <div className="d-flex flex-column gap-2">
                                            <div>
                                                <small className="text-muted">Numéro:</small>
                                                <div className="fw-bold text-primary">{releveCompte.numeroCompte}</div>
                                            </div>
                                            <div>
                                                <small className="text-muted">Intitulé:</small>
                                                <div>{releveCompte.intituleCompte}</div>
                                            </div>
                                            <div>
                                                <small className="text-muted">Type:</small>
                                                <Badge color={releveCompte.typeCompte === 'ACTIF' ? 'success' : releveCompte.typeCompte === 'PASSIF' ? 'info' : 'warning'}>
                                                    {releveCompte.typeCompte}
                                                </Badge>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col lg={12} className="mt-3">
                                        <Row className="text-center">
                                            <Col sm={3}>
                                                <small className="text-muted d-block">Solde Initial</small>
                                                <strong>{formatPrice(releveCompte.soldeInitial)}</strong>
                                            </Col>
                                            <Col sm={3}>
                                                <small className="text-muted d-block">Total Débit</small>
                                                <strong className="text-warning">{formatPrice(releveCompte.totalDebit)}</strong>
                                            </Col>
                                            <Col sm={3}>
                                                <small className="text-muted d-block">Total Crédit</small>
                                                <strong className="text-info">{formatPrice(releveCompte.totalCredit)}</strong>
                                            </Col>
                                            <Col sm={3}>
                                                <small className="text-muted d-block">Solde Actuel</small>
                                                <strong className={releveCompte.soldeActuel >= 0 ? 'text-success' : 'text-danger'}>
                                                    {formatPrice(releveCompte.soldeActuel)}
                                                </strong>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    )}
                </Col>

                <Col lg={8}>
                    {/* Right column: Errors and Loading */}
                    {error && (
                        <Alert color="danger" className="shadow-sm">
                            <div className="d-flex align-items-center">
                                <i className="mdi mdi-alert-circle me-2"></i>
                                {error}
                            </div>
                        </Alert>
                    )}

                    {loading.releveCompte && (
                        <Card className="shadow-sm">
                            <CardBody className="text-center py-5">
                                <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                                <div className="mt-3">
                                    <h6>Chargement du relevé de compte...</h6>
                                    <p className="text-muted mb-0">Veuillez patienter</p>
                                </div>
                            </CardBody>
                        </Card>
                    )}

                    {/* Account Entries - Expandable Table */}
                    {releveCompte && releveCompte.ecritures && releveCompte.ecritures.length > 0 ? (
                        <Card className="shadow-sm flex-1" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <CardHeader className="bg-light py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0">
                                        <FaFileAlt className="me-2 text-primary" />
                                        Écritures Comptables ({releveCompte.ecritures.length})
                                    </h6>
                                    <Badge color="primary" pill>
                                        {releveCompte.ecritures.length} écriture(s)
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardBody className="p-0 flex-1" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                <div 
                                    className="table-responsive flex-1" 
                                    style={{ 
                                        height: '400px',
                                        maxHeight: 'calc(100vh - 260px)',
                                        overflowY: 'auto',
                                        overflowX: 'auto'
                                    }}
                                >
                                    <Table className="table-hover mb-0" style={{ minWidth: '800px' }}>
                                        <thead className="table-dark sticky-top">
                                            <tr>
                                                <th className="border-0" style={{ minWidth: '100px' }}>Date</th>
                                                <th className="border-0" style={{ minWidth: '120px' }}>N° Écriture</th>
                                                <th className="border-0" style={{ minWidth: '200px' }}>Libellé</th>
                                                <th className="border-0 d-none d-md-table-cell" style={{ minWidth: '120px' }}>Journal</th>
                                                <th className="border-0 text-end" style={{ minWidth: '120px' }}>Débit</th>
                                                <th className="border-0 text-end" style={{ minWidth: '120px' }}>Crédit</th>
                                                <th className="border-0 d-none d-lg-table-cell" style={{ minWidth: '100px' }}>Référence</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {releveCompte.ecritures.map((ecriture, index) => (
                                                <tr key={index} className="align-middle">
                                                    <td>
                                                        <span className="badge badge-soft-secondary">
                                                            {new Date(ecriture.dateEcriture).toLocaleDateString('fr-DZ')}
                                                        </span>
                                                    </td>
                                                    <td className="fw-medium">{ecriture.numeroEcriture}</td>
                                                    <td className="text-wrap">{ecriture.libelle}</td>
                                                    <td className="d-none d-md-table-cell">
                                                        <small className="text-muted">{ecriture.journalLibelle}</small>
                                                    </td>
                                                    <td className="text-end">
                                                        {ecriture.montantDebit ? (
                                                            <span className="text-warning fw-medium">
                                                                {formatPrice(ecriture.montantDebit)}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td className="text-end">
                                                        {ecriture.montantCredit ? (
                                                            <span className="text-info fw-medium">
                                                                {formatPrice(ecriture.montantCredit)}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td className="d-none d-lg-table-cell">
                                                        <small className="text-muted">{ecriture.referenceDocument || '-'}</small>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                        </Card>
                    ) : releveCompte && (
                        <Card className="shadow-sm">
                            <CardBody className="text-center py-5">
                                <div className="text-muted">
                                    <FaFileAlt size={48} className="mb-3 opacity-50" />
                                    <h5>Aucune écriture trouvée</h5>
                                    <p className="mb-0">Aucune écriture comptable n'a été trouvée pour la période sélectionnée.</p>
                                </div>
                            </CardBody>
                        </Card>
                    )}
                </Col>
            </Row>

            <style jsx>{`
                .page-content {
                    background: #f8f9fa;
                    padding: 1rem;
                }
                
                .table-responsive {
                    border: 1px solid #dee2e6;
                    border-radius: 0.375rem;
                }
                
                .table-hover tbody tr:hover {
                    background-color: rgba(0,123,255,.075);
                }
                
                .badge-soft-secondary {
                    color: #6c757d;
                    background-color: rgba(108, 117, 125, 0.1);
                }
                
                .sticky-top {
                    position: sticky;
                    top: 0;
                    z-index: 10;
                }
                
                /* Custom scrollbar */
                .table-responsive::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                
                .table-responsive::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 4px;
                }
                
                .table-responsive::-webkit-scrollbar-thumb {
                    background: #c1c1c1;
                    border-radius: 4px;
                }
                
                .table-responsive::-webkit-scrollbar-thumb:hover {
                    background: #a8a8a8;
                }
                
                .flex-1 {
                    flex: 1;
                }
            `}</style>
        </div>
    );
};

export default ReleveCompte;
